import React, { useEffect, useState } from 'react';
import ImageWithBasePath from '../../core/common/imageWithBasePath';
import { Link, useNavigate } from 'react-router-dom';
import { all_routes } from '../router/all_routes';
import apiDefinitions from '../../core/api/apiDefinitions';
import './login.css';

const Login: React.FC = () => {
  const route = all_routes as any; // allow runtime existence checks for route keys
  const navigate = useNavigate();

  const [empNumber, setEmpNumber] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordVisible, setPasswordVisible] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('dataTheme');
    return saved ? saved === 'dark' : true;
  });

  // Reset password states
  const [isNewPasswordVisible, setNewPasswordVisible] = useState(false);
  const [isConfirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPasswordResetPopup, setShowPasswordResetPopup] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetError, setResetError] = useState<string | null>(null);

  // keep last-seen group around for post-reset routing
  const [lastGroup, setLastGroup] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem('menuOpened', 'Dashboard');
  }, []);

  const normalizeGroup = (g?: string) =>
    (g || '')
      .replace('_K', '')
      .trim()
      .toUpperCase();

  const navigateAfterLogin = (group?: string) => {
    const g = normalizeGroup(group);
    const isPrivileged = g === 'SERVICE_MANAGER' || g === 'HR_MANAGER';
    if (isPrivileged) {
      navigate(route.Dashboard);
    } else {
      navigate(route.booking);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    const payload = { empNumber, password };

    try {
      const response = await apiDefinitions.login(payload);
      const status = response?.data?.status;
      const statusDescription = response?.data?.statusDescription?.toLowerCase() || '';
      const { token, username, roles, group } = response?.data?.data || {};

      // store token + user data (respect "Remember Me")
      if (token) {
        const storage: Storage = rememberMe ? localStorage : sessionStorage;

        storage.setItem('token', token);
        storage.setItem('username', username || empNumber);
        storage.setItem('group', group || 'Unknown');
        storage.setItem('roles', JSON.stringify(roles || []));
        setLastGroup(group || null);
      }

      if (status === 'S1000' || (status === 'E3000' && statusDescription.includes('password reset required'))) {
        if (statusDescription.includes('password reset required')) {
          setShowPasswordResetPopup(true);
        } else {
          navigateAfterLogin(group);
        }
      } else {
        setError(response?.data?.statusDescription || 'Login failed. Please try again.');
      }
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.statusDescription ||
        'Invalid employee ID or password. Please try again.';
      if (!errorMsg.toLowerCase().includes('password reset required')) {
        setError(errorMsg);
      }
      if (err?.response?.status === 401) {
        setError('Invalid employee ID or password. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      setResetError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setResetError('Password must be at least 8 characters long.');
      return;
    }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setResetError('Password must contain uppercase, lowercase, and a number.');
      return;
    }

    setResetError(null);
    setIsLoading(true);

    try {
      const response = await apiDefinitions.initialPasswordReset({ password: newPassword });
      if (response?.data?.status === 'S1000') {
        setShowPasswordResetPopup(false);
        navigateAfterLogin(lastGroup || (localStorage.getItem('group') || sessionStorage.getItem('group') || undefined) as string | undefined);
      } else {
        setResetError(response?.data?.statusDescription || 'Failed to reset password.');
      }
    } catch (err: any) {
      const errorMsg =
        err?.response?.data?.statusDescription || 'An error occurred while resetting password.';
      setResetError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => setPasswordVisible((s) => !s);
  const toggleNewPasswordVisibility = () => setNewPasswordVisible((s) => !s);
  const toggleConfirmPasswordVisibility = () => setConfirmPasswordVisible((s) => !s);
  const toggleTheme = () => {
    setIsDarkMode((prev) => {
      const next = !prev;
      const theme = next ? 'dark' : 'light';
      localStorage.setItem('dataTheme', theme);
      document.documentElement.setAttribute('data-theme', theme);
      document.body.setAttribute('data-theme', theme);
      document.documentElement.setAttribute('data-bs-theme', theme);
      return next;
    });
  };

  const nightVideoSrc = `${process.env.PUBLIC_URL || ''}/assets/video/night_bg.mp4`;
  const dayVideoSrc = `${process.env.PUBLIC_URL || ''}/assets/video/day_bg.mp4`;

  return (
    <div className={`kiu-login-wrapper ${isDarkMode ? 'night-mode' : 'day-mode'}`}>
      {/* Background Video Player (Both preloaded with smooth cross-fade) */}
      <div className="kiu-video-bg-container">
        <video
          className={`kiu-video-bg ${isDarkMode ? 'active' : 'inactive'}`}
          src={nightVideoSrc}
          autoPlay
          loop
          muted
          playsInline
        />
        <video
          className={`kiu-video-bg ${!isDarkMode ? 'active' : 'inactive'}`}
          src={dayVideoSrc}
          autoPlay
          loop
          muted
          playsInline
        />
        <div className="kiu-video-overlay" />
      </div>

      {/* Header Bar */}
      <header className="kiu-login-header">
        <div className="kiu-brand-container">
          <ImageWithBasePath
            src="assets/img/kiu_crest_logo.png"
            className="kiu-crest-logo"
            alt="KIU Crest"
          />
          <div className="kiu-brand-text">
            <span className="kiu-brand-title">KIU</span>
            <span className="kiu-brand-subtitle">KIU UNIVERSITY</span>
          </div>
        </div>

        <div className="kiu-header-actions">
          {/* Day / Night Mode Toggle */}
          <button
            type="button"
            className="kiu-theme-toggle"
            onClick={toggleTheme}
            title={isDarkMode ? 'Switch to Day Mode' : 'Switch to Night Mode'}
          >
            <i className={`ti ${isDarkMode ? 'ti-sun' : 'ti-moon'}`} />
            <span>{isDarkMode ? 'Day Mode' : 'Night Mode'}</span>
          </button>

          {/* Secure Access Badge */}
          <div className="kiu-secure-badge">
            <i className="ti ti-shield-check" />
            <span>Secure Access</span>
            <span className="kiu-version-divider">|</span>
            <span>v1.0.0</span>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="kiu-login-main">
        {/* Left Side - Welcome Hero */}
        <section className="kiu-hero-left">
          <div className="kiu-hero-overline">WELCOME TO</div>
          <h1 className="kiu-hero-title">
            <span className="kiu-accent">KIU</span> Resource
            <br />
            Management System
          </h1>
          <div className="kiu-hero-bar" />
          <p className="kiu-hero-desc">
            A unified platform for academic excellence, operational efficiency and a smarter tomorrow.
          </p>

          {/* 3 Pillars / Highlights */}
          <div className="kiu-highlights-row">
            {/* People */}
            <div className="kiu-highlight-item">
              <div className="kiu-highlight-icon-box">
                <i className="ti ti-users" />
              </div>
              <div className="kiu-highlight-text">
                <span className="kiu-highlight-title">People</span>
                <span className="kiu-highlight-sub">Empower{'\n'}Our Community</span>
              </div>
            </div>

            {/* Process */}
            <div className="kiu-highlight-item">
              <div className="kiu-highlight-icon-box">
                <i className="ti ti-settings" />
              </div>
              <div className="kiu-highlight-text">
                <span className="kiu-highlight-title">Process</span>
                <span className="kiu-highlight-sub">Simplify{'\n'}Operations</span>
              </div>
            </div>

            {/* Progress */}
            <div className="kiu-highlight-item">
              <div className="kiu-highlight-icon-box">
                <i className="ti ti-chart-line" />
              </div>
              <div className="kiu-highlight-text">
                <span className="kiu-highlight-title">Progress</span>
                <span className="kiu-highlight-sub">Drive{'\n'}Excellence</span>
              </div>
            </div>
          </div>
        </section>

        {/* Right Side - Sign In Card */}
        <section className="kiu-card-container">
          <div className="kiu-login-card">
            <h2 className="kiu-card-title">Sign In</h2>
            <p className="kiu-card-subtitle">Access your KIU RMS account</p>

            <form onSubmit={handleSubmit}>
              {/* Employee ID */}
              <div className="kiu-form-group">
                <label className="kiu-form-label" htmlFor="employee-id">
                  Employee ID
                </label>
                <div className="kiu-input-wrapper">
                  <span className="kiu-input-icon">
                    <i className="ti ti-user" />
                  </span>
                  <input
                    id="employee-id"
                    type="text"
                    className="kiu-input-field"
                    placeholder="Enter your employee ID"
                    value={empNumber}
                    onChange={(e) => setEmpNumber(e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* Password */}
              <div className="kiu-form-group">
                <label className="kiu-form-label" htmlFor="password-input">
                  Password
                </label>
                <div className="kiu-input-wrapper">
                  <span className="kiu-input-icon">
                    <i className="ti ti-lock" />
                  </span>
                  <input
                    id="password-input"
                    type={isPasswordVisible ? 'text' : 'password'}
                    className="kiu-input-field"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                  <span
                    className="kiu-input-toggle"
                    onClick={togglePasswordVisibility}
                    title={isPasswordVisible ? 'Hide password' : 'Show password'}
                  >
                    <i className={`ti ${isPasswordVisible ? 'ti-eye' : 'ti-eye-off'}`} />
                  </span>
                </div>
              </div>

              {/* Error Message */}
              {error && (
                <div className="kiu-alert-error">
                  <i className="ti ti-alert-circle" />
                  <span>{error}</span>
                </div>
              )}

              {/* Options Row (Remember Me & Forgot Password) */}
              <div className="kiu-form-options">
                <label className="kiu-remember-box" htmlFor="checkbox-remember">
                  <input
                    type="checkbox"
                    id="checkbox-remember"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                  />
                  <span>Remember me</span>
                </label>
                <Link
                  to={route.forgotPassword || '#'}
                  className="kiu-forgot-link"
                  onClick={(e) => {
                    if (!route.forgotPassword) {
                      e.preventDefault();
                      alert('Please contact the IT administrator to reset your password.');
                    }
                  }}
                >
                  Forgot password?
                </Link>
              </div>

              {/* Primary Submit Button */}
              <button
                type="submit"
                className="kiu-btn-submit"
                disabled={isLoading}
              >
                <span>{isLoading ? 'Signing In...' : 'Sign In'}</span>
                {!isLoading && <i className="ti ti-arrow-right" />}
              </button>
            </form>

            {/* Bottom Card Architectural Watermark */}
            <div className="kiu-card-watermark">
              <ImageWithBasePath
                src="assets/img/kiu_building_watermark.png"
                className="kiu-watermark-img"
                alt="KIU Building"
              />
              <span className="kiu-watermark-text">INSPIRING EXCELLENCE</span>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="kiu-login-footer">
        <p className="kiu-footer-copy">© 2025 KIU. All rights reserved.</p>
        <div className="kiu-footer-links">
          <a href="#privacy" onClick={(e) => e.preventDefault()}>Privacy Policy</a>
          <span className="kiu-footer-separator">|</span>
          <a href="#help" onClick={(e) => e.preventDefault()}>Help</a>
          <span className="kiu-footer-separator">|</span>
          <a href="#contact" onClick={(e) => e.preventDefault()}>Contact IT</a>
        </div>
      </footer>

      {/* Password Reset Popup Modal */}
      {showPasswordResetPopup && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)', zIndex: 1050 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '20px', border: 'none', overflow: 'hidden', boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }}>
              <div className="modal-header" style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '18px 24px' }}>
                <h5 className="modal-title" style={{ fontWeight: 700, color: '#0f172a', fontSize: '18px' }}>
                  Set New Password
                </h5>
              </div>
              <div className="modal-body" style={{ padding: '24px' }}>
                <p style={{ color: '#64748b', fontSize: '13.5px', marginBottom: '18px' }}>
                  Your account requires an initial password change before proceeding. Please enter a strong password with uppercase, lowercase, and numbers.
                </p>
                <form onSubmit={handlePasswordReset}>
                  <div className="mb-3">
                    <label className="col-form-label" style={{ fontWeight: 600, fontSize: '13px', color: '#334155' }}>
                      New Password
                    </label>
                    <div className="pass-group" style={{ position: 'relative' }}>
                      <input
                        type={isNewPasswordVisible ? 'text' : 'password'}
                        className="form-control"
                        style={{ borderRadius: '10px', paddingRight: '40px' }}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        placeholder="At least 8 characters"
                        required
                      />
                      <span
                        className={`ti toggle-password ${isNewPasswordVisible ? 'ti-eye' : 'ti-eye-off'}`}
                        onClick={toggleNewPasswordVisibility}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          cursor: 'pointer',
                          color: '#64748b',
                        }}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="col-form-label" style={{ fontWeight: 600, fontSize: '13px', color: '#334155' }}>
                      Confirm Password
                    </label>
                    <div className="pass-group" style={{ position: 'relative' }}>
                      <input
                        type={isConfirmPasswordVisible ? 'text' : 'password'}
                        className="form-control"
                        style={{ borderRadius: '10px', paddingRight: '40px' }}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter password"
                        required
                      />
                      <span
                        className={`ti toggle-password ${isConfirmPasswordVisible ? 'ti-eye' : 'ti-eye-off'}`}
                        onClick={toggleConfirmPasswordVisibility}
                        style={{
                          position: 'absolute',
                          right: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          cursor: 'pointer',
                          color: '#64748b',
                        }}
                      />
                    </div>
                  </div>

                  {resetError && (
                    <div className="alert alert-danger" style={{ borderRadius: '10px', fontSize: '13px' }}>
                      {resetError}
                    </div>
                  )}

                  <button
                    type="submit"
                    className="kiu-btn-submit mt-3"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Resetting...' : 'Reset Password'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Login;
